import { ApiKeyStore } from '@/capture/ApiKeyStore';
import type { FloraCatalog } from '@/flora/FloraCatalog';
import { describeFrame, type MapFrame } from '@/geo';
import type { LandscapeProject } from '@/project/Project';
import { surfaceLayerIds, type SurfaceLayerId } from '@/terrain/TerrainSurface';
import {
  DEFAULT_SITE_PLAN_MODEL,
  MAX_IMAGES,
  PIPELINE_STAGES,
  SITE_PLAN_MODELS,
  SitePipelineRunner,
  artifactForDisplay,
  compilePipeline,
  describeReferenceTags,
  stageMeta,
  suggestReferenceTags,
  type PipelineCompileResult,
  type PipelineStageId,
  type StageRun,
  type SuggestTagsResult,
} from '@/reference';
import { clear, el } from './dom';
import { icon, type IconName } from './Icons';
import { dialogButton, textInput } from './Dialog';
import { overlayShapes, svgPoints, type OverlayShape } from './PipelineOverlay';
import { ReferenceTagger } from './ReferenceTagger';
import { FrameSelector } from './FrameSelector';

/**
 * The staged-pipeline inspector: a full-surface view of how a photograph becomes a site.
 *
 * The single-call reconstruction gave a result and a summary. That is enough to *use* and useless to
 * *improve*: when a site came out wrong there was one opaque JSON blob and no way to attribute the
 * error. This view exists for the opposite purpose — it is a developer's instrument for seeing which
 * judgement failed, aimed at making the algorithm better rather than making one site.
 *
 * Everything a stage touched is kept and shown:
 *
 * - **Overlay** — the stage's own geometry drawn over the reference photograph. This is the view
 *   that actually finds bugs, because a misplaced driveway is obvious in the frame and invisible in
 *   a number.
 * - **Artifact** — the parsed, canonical output the compiler will consume.
 * - **Reply** — the raw model text, kept even when it failed to parse, which is nearly always
 *   enough to see why it failed.
 * - **Prompt** — exactly what was sent, including the prior artifacts, viewable *before* the call.
 *
 * And every stage re-runs on its own, or re-runs itself and everything downstream, so a hypothesis
 * about one stage can be tested without paying for the other four.
 *
 * ## The two steps before stage 1 are the designer's
 *
 * The stage column starts with work no model does, and neither step blocks a run:
 *
 * - **Select frame** (`ui/FrameSelector.ts`) — browse a map to the plot and cut the reference image
 *   from it. Transformative when used: a map capture carries its own projection, so the site extent
 *   becomes measured rather than estimated and stage 1's scale hunt is replaced by an instruction
 *   not to perform one.
 * - **Tag the photograph** (`reference/ReferenceTag.ts`) — worth more before stage 1 than any amount
 *   of re-running afterwards, and now openable with a suggested draft rather than a blank panel.
 *
 * The frame step also carries the site-data seam — property boundary and topography — which is where
 * imported ground measurements can replace stage 2 outright (`runner.setArtifact`).
 *
 * ## One place to look for what to do next
 *
 * Seven stages, four detail tabs, a dozen imports and two optional data seams is a lot of surface,
 * and the first version scattered the actual *flow* through it: the run started from a button in the
 * top-right corner and finished at a different button in the bottom-right, with the five stages'
 * progress readable only by comparing small grey words down the left edge.
 *
 * So the surface now states the flow three times over, in three registers:
 *
 * - a **progress ribbon** across the header — every step as a chip, filled when done, pulsing when
 *   running, so where the run has got to is legible from across the room;
 * - the **stage column**, which marks the step you would sensibly do next rather than leaving all
 *   seven looking equally available;
 * - the **action rail** in the footer, which is the only primary button on the surface. It is
 *   whatever comes next — run, cancel, or create the site — so "what do I press" never has two
 *   answers in two corners.
 */

const MODEL_STORAGE_KEY = 'landscape-glade.site-plan-model';
const OTHER_MODEL = '__other__';

type DetailTab = 'overlay' | 'artifact' | 'reply' | 'prompt';

/** What the detail pane is showing: one of the two designer steps, or one of the five stages. */
type Selection = 'frame' | 'tags' | PipelineStageId;

export interface PipelineInspectorDeps {
  /** Element to mount into — the HUD root. */
  host: HTMLElement;
  catalog: FloraCatalog;
  /** Called when the user accepts the compiled site. */
  onCreate: (project: LandscapeProject) => void;
  onClose?: () => void;
  /** Injected for tests. */
  keyStore?: ApiKeyStore;
  runnerFactory?: (deps: ConstructorParameters<typeof SitePipelineRunner>[0]) => SitePipelineRunner;
}

interface Attachment {
  file: File;
  url: string;
}

export class PipelineInspector {
  readonly root: HTMLElement;

  private readonly keyStore: ApiKeyStore;
  private readonly runner: SitePipelineRunner;
  private readonly unsubscribe: () => void;
  /**
   * Long-lived, and re-appended rather than rebuilt on every render — it holds a half-typed tag and
   * the caret that goes with it, neither of which survives being reconstructed under the user.
   */
  private readonly tagger: ReferenceTagger;
  /** Long-lived for the same reason as the tagger: it holds a map position and a search box. */
  private readonly frameSelector: FrameSelector;

  private readonly attachments: Attachment[] = [];
  private direction = '';
  private model: string;
  /** The georeference of the captured frame, when the reference came from the map rather than a file. */
  private frame: MapFrame | null = null;

  private selected: Selection = 'frame';
  private tab: DetailTab = 'overlay';
  private compiled: PipelineCompileResult | null = null;
  private compileError: string | null = null;
  private seed = 1;
  private disposed = false;

  constructor(private readonly deps: PipelineInspectorDeps) {
    this.keyStore = deps.keyStore ?? new ApiKeyStore();
    this.model = readStoredModel();

    this.tagger = new ReferenceTagger({
      onChange: () => this.render(),
      suggest: (signal) => this.suggestTags(signal),
      suggestBlockedReason: () => this.suggestBlockedReason(),
    });
    this.frameSelector = new FrameSelector({
      onCapture: (file, frame) => this.adoptFrame(file, frame),
      onTerrain: (artifact, frame) => {
        // Measured ground answers stage 2 better than any reading of a photograph, so it does not
        // inform the stage — it replaces it.
        this.runner.setArtifact('terrain', artifact, `imported topography (${describeFrame(frame)})`);
        this.recompile();
        this.render();
      },
      onStructures: (artifact, detail, frame) => {
        // Surveyed footprints answer stage 5's hardest question better than any reading of a roof
        // plan, so they replace the stage rather than informing it — and the import report travels
        // with them into the Reply pane, where the evidence belongs.
        this.runner.setArtifact('structures', artifact, `OpenStreetMap (${describeFrame(frame)})`, detail);
        this.recompile();
        this.render();
      },
      onChange: () => this.render(),
    });

    const runnerDeps = {
      catalog: deps.catalog,
      apiKey: () => this.keyStore.get(),
      model: () => this.model,
      images: () => this.attachments.map((attachment) => attachment.file),
      direction: () => this.direction,
      tags: () => this.tagger.tags,
      frame: () => this.frame,
    };
    this.runner = deps.runnerFactory
      ? deps.runnerFactory(runnerDeps)
      : new SitePipelineRunner(runnerDeps);

    // Re-render on every stage transition so a two-minute run shows progress as it happens.
    this.unsubscribe = this.runner.onChange(() => {
      this.recompile();
      this.render();
    });

    this.root = el('div', { class: 'lg-pipeline', attrs: { role: 'dialog', 'aria-label': 'Site pipeline' } });
    deps.host.appendChild(this.root);
    this.render();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.runner.cancel();
    this.unsubscribe();
    this.tagger.dispose();
    this.frameSelector.dispose();
    for (const attachment of this.attachments) URL.revokeObjectURL(attachment.url);
    this.attachments.length = 0;
    this.root.remove();
  }

  /* --------------------------------- Rendering --------------------------------- */

  private render(): void {
    if (this.disposed) return;
    clear(this.root);
    this.root.appendChild(this.header());
    this.root.appendChild(
      el('div', { class: 'lg-pipeline__body' }, this.sidebar(), this.detail()),
    );
    this.root.appendChild(this.footer());
  }

  private header(): HTMLElement {
    return el(
      'header',
      { class: 'lg-pipeline__header' },
      el(
        'div',
        { class: 'lg-pipeline__title' },
        el('span', { class: 'lg-pipeline__titleicon', html: icon('plant', 18) }),
        el('h2', { text: 'Site pipeline' }),
      ),
      this.progressRibbon(),
      el(
        'div',
        { class: 'lg-pipeline__headeractions' },
        dialogButton({
          label: 'Close',
          title: 'Close the inspector',
          onClick: () => {
            this.deps.onClose?.();
            this.dispose();
          },
        }),
      ),
    );
  }

  /**
   * Every step as a chip, in order, carrying its own status.
   *
   * The one view that answers "where has this got to" without reading anything. Statuses are the
   * same four the stage column shows and the colours are the same colours, so the ribbon is a
   * summary of the column rather than a second opinion about it — and each chip selects its step,
   * because a progress indicator you cannot click is a place people try to click.
   */
  private progressRibbon(): HTMLElement {
    const ribbon = el('div', {
      class: 'lg-pipeline__ribbon',
      attrs: { role: 'list', 'aria-label': 'Pipeline progress' },
    });

    const steps: { id: Selection; label: string; status: string; index: string }[] = [
      {
        id: 'frame',
        label: 'Frame',
        status: this.frame !== null ? 'done' : this.attachments.length > 0 ? 'skipped' : 'todo',
        index: '·',
      },
      {
        id: 'tags',
        label: 'Tags',
        status: this.tagger.summary.total > 0 ? 'done' : 'skipped',
        index: '·',
      },
      ...PIPELINE_STAGES.map((meta, index) => {
        const run = this.runner.state.stages.find((stage) => stage.id === meta.id);
        return {
          id: meta.id,
          label: meta.title,
          status: this.runner.isSupplied(meta.id) ? 'measured' : (run?.status ?? 'pending'),
          index: String(index + 1),
        };
      }),
    ];

    for (const step of steps) {
      ribbon.appendChild(
        el(
          'button',
          {
            class: `lg-pipeline__step${this.selected === step.id ? ' is-selected' : ''}`,
            dataset: { status: step.status },
            attrs: {
              type: 'button',
              role: 'listitem',
              'data-autofocus': 'skip',
              'aria-current': step.status === 'running',
              title: `${step.label} — ${RIBBON_STATUS[step.status] ?? step.status}`,
            },
            on: {
              click: () => {
                this.selected = step.id;
                this.render();
              },
            },
          },
          el('span', { class: 'lg-pipeline__stepdot', text: step.status === 'done' || step.status === 'measured' ? '✓' : step.index }),
          el('span', { class: 'lg-pipeline__steplabel', text: step.label }),
        ),
      );
    }

    return ribbon;
  }

  /* ---------------------------------- Sidebar ---------------------------------- */

  private sidebar(): HTMLElement {
    const side = el('aside', { class: 'lg-pipeline__side' });
    side.appendChild(this.inputSection());
    side.appendChild(el('h3', { class: 'lg-section__title', text: 'Before the run  ·  both optional' }));
    side.appendChild(this.frameRow());
    side.appendChild(this.tagRow());
    side.appendChild(el('h3', { class: 'lg-section__title', text: 'The five stages' }));

    const state = this.runner.state;
    for (const [index, meta] of PIPELINE_STAGES.entries()) {
      const run = state.stages.find((stage) => stage.id === meta.id);
      side.appendChild(this.stageRow(index, run));
    }
    return side;
  }

  /**
   * The step it would make sense to do next.
   *
   * Marked in the column so seven rows do not read as seven equally-good options. Tagging is offered
   * as the next step while nothing is tagged and stage 1 has not run — it is optional, and it is
   * still the thing most worth doing at that moment, which is exactly what a suggestion is for.
   */
  private nextStep(): Selection | null {
    if (this.attachments.length === 0) return 'frame';
    const state = this.runner.state;
    const segment = state.stages.find((stage) => stage.id === 'segment');
    if (this.tagger.summary.total === 0 && segment?.status !== 'done') return 'tags';
    return (
      PIPELINE_STAGES.find(
        (meta) => state.stages.find((stage) => stage.id === meta.id)?.status !== 'done',
      )?.id ?? null
    );
  }

  /**
   * The frame step, first in the column.
   *
   * Marked optional rather than required, and it is the only row here that is. A site under canopy,
   * a courtyard, a roof terrace — plenty of real work has no useful aerial imagery, and a required
   * map step would simply block it. What the row does say is what taking it buys, because "the
   * extent stops being a guess" is not obvious from the words "select frame".
   */
  private frameRow(): HTMLElement {
    const active = this.selected === 'frame';
    const captured = this.frame !== null;

    return el(
      'div',
      {
        class: `lg-stage lg-stage--setup${active ? ' is-active' : ''}${
          this.nextStep() === 'frame' ? ' is-next' : ''
        }`,
        dataset: { status: captured ? 'done' : 'optional' },
      },
      el(
        'button',
        {
          class: 'lg-stage__open',
          attrs: { type: 'button', 'data-autofocus': 'skip' },
          title: 'Browse a map to the plot and take the reference image from it.',
          on: {
            click: () => {
              this.selected = 'frame';
              this.render();
            },
          },
        },
        el(
          'span',
          { class: 'lg-stage__head' },
          el('span', { class: 'lg-stage__index', html: icon('top', 12) }),
          el('span', { class: 'lg-stage__name', text: 'Select frame' }),
          statusPill(captured ? 'done' : 'optional', captured ? 'captured' : 'optional'),
        ),
        el('span', {
          class: 'lg-stage__summary',
          text: captured
            ? 'The extent is measured, so stage 1 is told not to estimate it. Buildings and ground can be measured here too.'
            : 'Browse a map instead of uploading. Also where measured ground, buildings, walls and fences come from.',
        }),
        captured &&
          el('span', { class: 'lg-stage__metrics', text: describeFrame(this.frame as MapFrame) }),
      ),
    );
  }

  /**
   * Step 0, in the stage column where it belongs.
   *
   * Listed as a step rather than tucked into the inputs because it *is* one: it happens before the
   * stages and its output is carried into all five prompts. It no longer blocks them — a step that
   * can be drafted by a model is a step a gate cannot justify — so the row says "optional" plainly
   * and the panel behind it makes the case.
   */
  private tagRow(): HTMLElement {
    const active = this.selected === 'tags';
    const summary = this.tagger.summary;

    return el(
      'div',
      {
        class: `lg-stage lg-stage--setup${active ? ' is-active' : ''}${
          this.nextStep() === 'tags' ? ' is-next' : ''
        }`,
        dataset: { status: summary.total > 0 ? 'done' : 'optional' },
      },
      el(
        'button',
        {
          class: 'lg-stage__open',
          attrs: { type: 'button', 'data-autofocus': 'skip' },
          title: 'Say what is in the photograph before the stages read it — or have it suggested.',
          on: {
            click: () => {
              this.selected = 'tags';
              this.render();
            },
          },
        },
        el(
          'span',
          { class: 'lg-stage__head' },
          el('span', { class: 'lg-stage__index', html: icon('tag', 12) }),
          el('span', { class: 'lg-stage__name', text: 'Tag the photograph' }),
          statusPill(
            summary.total > 0 ? 'done' : 'optional',
            summary.total > 0 ? `${summary.total} tags` : 'optional',
          ),
        ),
        el('span', {
          class: 'lg-stage__summary',
          text: 'What the frame is of, in your words. Sent to every stage below.',
        }),
        el('span', { class: 'lg-stage__metrics', text: describeReferenceTags(this.tagger.tags) }),
      ),
    );
  }

  private stageRow(index: number, run: StageRun | undefined): HTMLElement {
    if (!run) return el('div');
    const meta = stageMeta(run.id);
    const active = this.selected === run.id;

    const metrics: string[] = [];
    if (run.durationMs !== undefined) metrics.push(`${(run.durationMs / 1000).toFixed(1)} s`);
    if (run.usage) {
      metrics.push(`${formatTokens(run.usage.promptTokens + run.usage.completionTokens)} tok`);
    }

    const supplied = this.runner.isSupplied(run.id);
    const status = supplied ? 'measured' : run.status;

    return el(
      'div',
      {
        class: `lg-stage${active ? ' is-active' : ''}${this.nextStep() === run.id ? ' is-next' : ''}`,
        dataset: { status },
      },
      el(
        'button',
        {
          class: 'lg-stage__open',
          attrs: { type: 'button', 'data-autofocus': 'skip' },
          title: meta.summary,
          on: {
            click: () => {
              this.selected = run.id;
              this.render();
            },
          },
        },
        el(
          'span',
          { class: 'lg-stage__head' },
          el('span', {
            class: 'lg-stage__index',
            text: status === 'done' || supplied ? '✓' : String(index + 1),
          }),
          el('span', { class: 'lg-stage__name', text: meta.title }),
          statusPill(status, supplied ? 'measured' : statusLabel(run.status)),
        ),
        el('span', { class: 'lg-stage__summary', text: meta.summary }),
        metrics.length > 0 && el('span', { class: 'lg-stage__metrics', text: metrics.join('  ·  ') }),
        materialsRow(run),
        run.error !== undefined && el('span', { class: 'lg-stage__error', text: run.error }),
      ),
      el(
        'div',
        { class: 'lg-stage__actions' },
        el('button', {
          class: 'lg-iconbtn',
          html: icon('rotate', 14),
          title: `Re-run ${meta.title.toLowerCase()} only`,
          attrs: {
            type: 'button',
            'aria-label': `Re-run ${meta.title}`,
            'data-autofocus': 'skip',
            ...(this.runner.state.busy || !this.canRun() ? { disabled: 'true' } : {}),
          },
          on: { click: () => void this.runner.runStage(run.id) },
        }),
        el('button', {
          class: 'lg-iconbtn',
          html: icon('chevron', 14),
          title: `Re-run from ${meta.title.toLowerCase()} onward`,
          attrs: {
            type: 'button',
            'aria-label': `Re-run from ${meta.title} onward`,
            'data-autofocus': 'skip',
            ...(this.runner.state.busy || !this.canRun() ? { disabled: 'true' } : {}),
          },
          on: { click: () => void this.runner.run(run.id) },
        }),
      ),
    );
  }

  private inputSection(): HTMLElement {
    const input = el('input', {
      attrs: { type: 'file', accept: 'image/*', multiple: true },
      style: { display: 'none' },
      on: {
        change: (event) => {
          const target = event.target as HTMLInputElement;
          this.addFiles(target.files);
          target.value = '';
        },
      },
    });

    const thumbs = el('div', { class: 'lg-pipeline__thumbs' });
    for (const [index, attachment] of this.attachments.entries()) {
      thumbs.appendChild(
        el(
          'div',
          { class: 'lg-pipeline__thumb', title: attachment.file.name },
          el('img', { attrs: { src: attachment.url, alt: attachment.file.name } }),
          el('button', {
            class: 'lg-pipeline__remove',
            html: icon('delete', 12),
            title: `Remove ${attachment.file.name}`,
            attrs: { type: 'button', 'aria-label': `Remove ${attachment.file.name}`, 'data-autofocus': 'skip' },
            on: { click: () => this.removeAt(index) },
          }),
        ),
      );
    }

    const drop = el(
      'div',
      {
        class: `lg-pipeline__drop${this.attachments.length > 0 ? ' is-filled' : ''}`,
        on: {
          click: () => input.click(),
          dragover: (event) => {
            event.preventDefault();
            drop.classList.add('is-over');
          },
          dragleave: () => drop.classList.remove('is-over'),
          drop: (event) => {
            event.preventDefault();
            drop.classList.remove('is-over');
            this.addFiles(event.dataTransfer?.files ?? null);
          },
        },
      },
      this.attachments.length === 0
        ? el('span', { class: 'lg-pipeline__droptext', text: 'Drop reference photographs, or click' })
        : thumbs,
    );

    const directionField = el('textarea', {
      class: 'lg-pipeline__direction',
      attrs: {
        rows: 2,
        placeholder: 'Direction the photo cannot show — "the hedge is beech", "this is Vermont"',
        'aria-label': 'Additional direction for every stage',
      },
      on: {
        input: (event) => {
          this.direction = (event.target as HTMLTextAreaElement).value;
        },
      },
    });
    directionField.value = this.direction;

    return el(
      'div',
      { class: 'lg-pipeline__inputs' },
      el('span', {
        class: 'lg-field__label',
        text: `Reference (${this.attachments.length}/${MAX_IMAGES})`,
      }),
      drop,
      input,
      directionField,
      this.keyRow(),
    );
  }

  private keyRow(): HTMLElement {
    const hasKey = this.keyStore.has();
    const keyInput = textInput({
      value: '',
      placeholder: hasKey ? 'Key stored' : 'sk-…',
      ariaLabel: 'OpenAI API key',
      onInput: (value) => {
        if (value.trim().length > 0) {
          this.keyStore.set(value);
          this.render();
        }
      },
    });
    keyInput.type = 'password';

    const listed = SITE_PLAN_MODELS.some((option) => option.id === this.model);
    const select = el('select', {
      class: 'lg-input',
      attrs: { 'aria-label': 'Vision model' },
      on: {
        change: (event) => {
          const value = (event.target as HTMLSelectElement).value;
          this.model = value === OTHER_MODEL ? '' : value;
          storeModel(this.model);
          this.render();
        },
      },
    });
    for (const option of SITE_PLAN_MODELS) {
      select.appendChild(
        el('option', {
          text: option.label,
          attrs: { value: option.id, selected: option.id === this.model },
        }),
      );
    }
    select.appendChild(el('option', { text: 'Other…', attrs: { value: OTHER_MODEL, selected: !listed } }));
    select.value = listed ? this.model : OTHER_MODEL;

    return el(
      'div',
      { class: 'lg-pipeline__keys' },
      keyInput,
      select,
      !listed &&
        textInput({
          value: this.model,
          placeholder: 'model id',
          ariaLabel: 'Custom model id',
          onInput: (value) => {
            this.model = value.trim();
            storeModel(this.model);
          },
        }),
    );
  }

  /* ----------------------------------- Detail ----------------------------------- */

  private detail(): HTMLElement {
    if (this.selected === 'frame') {
      return el(
        'section',
        { class: 'lg-pipeline__detail' },
        el(
          'div',
          { class: 'lg-pipeline__detailhead' },
          el('h3', { class: 'lg-section__title', text: 'Select frame' }),
          el('span', {
            class: 'lg-pipeline__subtitle',
            text: this.frame
              ? describeFrame(this.frame)
              : 'A map capture knows its own scale — an uploaded photograph does not',
          }),
        ),
        this.frameSelector.root,
      );
    }

    if (this.selected === 'tags') {
      return el(
        'section',
        { class: 'lg-pipeline__detail' },
        el(
          'div',
          { class: 'lg-pipeline__detailhead' },
          el('h3', { class: 'lg-section__title', text: 'Tag the photograph' }),
          el('span', {
            class: 'lg-pipeline__subtitle',
            text: 'Optional, and the cheapest correction in the pipeline',
          }),
        ),
        this.tagger.root,
      );
    }

    const selected = this.selected;
    const run = this.runner.state.stages.find((stage) => stage.id === selected);
    const meta = stageMeta(selected);

    const tabs = el(
      'div',
      { class: 'lg-pipeline__tabs' },
      ...(['overlay', 'artifact', 'reply', 'prompt'] as DetailTab[]).map((id) =>
        el('button', {
          class: `lg-pipeline__tab${this.tab === id ? ' is-active' : ''}`,
          text: TAB_LABELS[id],
          attrs: { type: 'button', 'aria-pressed': this.tab === id, 'data-autofocus': 'skip' },
          on: {
            click: () => {
              this.tab = id;
              this.render();
            },
          },
        }),
      ),
    );

    return el(
      'section',
      { class: 'lg-pipeline__detail' },
      el(
        'div',
        { class: 'lg-pipeline__detailhead' },
        el('h3', { class: 'lg-section__title', text: meta.title }),
        tabs,
      ),
      this.tabBody(selected, run),
    );
  }

  private tabBody(stage: PipelineStageId, run: StageRun | undefined): HTMLElement {
    switch (this.tab) {
      case 'overlay':
        return this.overlayView(stage);
      case 'artifact':
        return codeBlock(
          run?.artifact !== undefined
            ? // Elided, or a measured terrain lattice buries every field worth reading under
              // twenty thousand numbers. The summary states how many were measured.
              JSON.stringify(artifactForDisplay(run.artifact), null, 2)
            : 'This stage has not produced an artifact yet.',
        );
      case 'reply':
        return codeBlock(run?.raw ?? 'Nothing has come back from this stage yet.');
      case 'prompt':
        // Built live rather than read from the run, so the prompt can be reviewed before spending
        // anything — and so it reflects any upstream artifact that has changed since.
        return codeBlock(promptText(this.safePrompt(stage)));
    }
  }

  /**
   * The reference photograph with this stage's geometry drawn over it.
   *
   * SVG on a fixed 1000×1000 viewBox with `preserveAspectRatio="none"`, laid directly over the
   * image: both then stretch identically to whatever box the layout gives them, so a shape stays
   * registered with the pixel it describes at any window size.
   */
  private overlayView(stage: PipelineStageId): HTMLElement {
    const photo = this.attachments[0];
    if (!photo) {
      return el('div', {
        class: 'lg-pipeline__empty',
        text: 'Add a reference photograph to see the overlay.',
      });
    }

    // A captured frame knows the site extent without stage 1 having run, so measured structures and
    // terrain draw over the photograph before anything has been sent to a model.
    const shapes = overlayShapes(
      stage,
      this.runner.artifacts,
      this.frame
        ? { widthMeters: this.frame.widthMeters, lengthMeters: this.frame.lengthMeters }
        : undefined,
    );
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 1000 1000');
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.setAttribute('class', 'lg-pipeline__svg');

    for (const shape of shapes) svg.appendChild(shapeNode(shape));

    return el(
      'div',
      { class: 'lg-pipeline__overlay' },
      el(
        'div',
        { class: 'lg-pipeline__frame' },
        el('img', { attrs: { src: photo.url, alt: 'Reference photograph' } }),
        svg,
      ),
      shapes.length === 0
        ? el('p', {
            class: 'lg-hint',
            text:
              stage === 'segment'
                ? 'Run the pipeline to see the segmentation drawn over the photograph.'
                : this.frame === null && this.runner.artifacts.segment === undefined
                  ? 'Nothing to draw yet. Capture a frame from the map, or run stage 1, so world ' +
                    'coordinates have an extent to be drawn against.'
                  : 'Nothing to draw for this stage yet.',
          })
        : this.legend(shapes),
    );
  }

  private legend(shapes: readonly OverlayShape[]): HTMLElement {
    const legend = el('div', { class: 'lg-pipeline__legend' });
    const seen = new Set<string>();
    for (const shape of shapes) {
      const key = `${shape.label}|${shape.color}`;
      if (seen.has(key)) continue;
      seen.add(key);
      legend.appendChild(
        el(
          'span',
          { class: 'lg-pipeline__legenditem', title: shape.detail ?? shape.label },
          el('span', { class: 'lg-pipeline__swatch', style: { background: shape.color } }),
          el('span', { text: shape.label }),
        ),
      );
    }
    return legend;
  }

  /* ----------------------------------- Footer ----------------------------------- */

  private footer(): HTMLElement {
    const report = this.compiled?.report;

    const stats = el('div', { class: 'lg-pipeline__stats' });
    if (report) {
      stats.appendChild(stat('Plants', `${report.plantCount} · ${report.speciesCount} species`));
      stats.appendChild(
        stat('Built', `${report.siteObjectCount} objects · ${report.massCount} masses`),
      );
      stats.appendChild(stat('Relief', `${report.reliefMeters.toFixed(2)} m · ${describeGround(report)}`));
      stats.appendChild(stat('Painted', describeCoverage(report.coverage)));
      stats.appendChild(stat('Groups', String(report.groupCount)));
    } else {
      stats.appendChild(
        el('span', {
          class: 'lg-hint',
          text:
            this.compileError ??
            'Run the stages to build a site. The compiler runs locally after each one, so this fills in as it goes.',
        }),
      );
    }

    const warnings = report?.warnings ?? [];
    const warningList = el('ul', { class: 'lg-pipeline__warnings' });
    for (const warning of warnings.slice(0, 8)) {
      warningList.appendChild(el('li', { text: warning.message }));
    }
    if (warnings.length > 8) {
      warningList.appendChild(el('li', { text: `…and ${warnings.length - 8} more.` }));
    }

    return el(
      'footer',
      { class: 'lg-pipeline__footer' },
      el('div', { class: 'lg-pipeline__readout' }, stats, warnings.length > 0 && warningList),
      this.actionRail(),
    );
  }

  /**
   * The only primary button on the surface, and the sentence saying what it will do.
   *
   * Everything about the run happens here: starting it, watching it, cancelling it, and taking the
   * site at the end. That is the whole point — the two halves of the flow used to live in opposite
   * corners, so finishing a run meant hunting for a second button in a place the first one had
   * taught you to ignore.
   *
   * Secondary actions are the ones that are genuinely optional at that moment (re-roll the layout,
   * run the whole thing again) and they sit to the left of the primary, quieter.
   */
  private actionRail(): HTMLElement {
    const state = this.runner.state;
    const action = this.nextAction();

    const primary = dialogButton({
      label: action.label,
      primary: true,
      icon: action.icon,
      title: action.title,
      onClick: action.onClick,
      disabled: action.disabled,
    });
    if (action.urgent) primary.classList.add('is-urgent');

    return el(
      'div',
      { class: 'lg-pipeline__act', dataset: { tone: action.tone } },
      el(
        'div',
        { class: 'lg-pipeline__actwhat' },
        el('span', { class: 'lg-pipeline__actstep', text: action.step }),
        el('span', { class: 'lg-pipeline__acthint', text: action.hint }),
      ),
      el(
        'div',
        { class: 'lg-pipeline__actbtns' },
        state.busy &&
          dialogButton({
            label: 'Cancel',
            title: 'Stop after the stage that is running',
            onClick: () => this.runner.cancel(),
          }),
        !state.busy &&
          this.compiled !== null &&
          dialogButton({
            label: 'Re-roll layout',
            icon: 'rotate',
            title: 'Redistribute the planting from the same artifacts — free, no API call',
            onClick: () => {
              this.seed = Math.floor(Math.random() * 100000) + 1;
              this.recompile();
              this.render();
            },
          }),
        !state.busy &&
          this.runner.complete &&
          dialogButton({
            label: 'Run all again',
            icon: 'plant',
            title: 'Re-run all five stages from the start',
            onClick: () => void this.runner.run(),
            disabled: !this.canRun(),
          }),
        primary,
      ),
    );
  }

  /**
   * What the surface is asking for right now, as one object.
   *
   * One function rather than a button that reads its own conditions, so the label, the icon, the
   * tooltip, the step counter and the explanatory line can never disagree — which is exactly how the
   * previous version came to say "Run all stages" above a footer offering "Create site" on a run
   * that had not started.
   */
  private nextAction(): {
    label: string;
    icon: IconName;
    title: string;
    step: string;
    hint: string;
    tone: 'blocked' | 'run' | 'busy' | 'create';
    disabled: boolean;
    urgent: boolean;
    onClick: () => void;
  } {
    const state = this.runner.state;
    const running = state.stages.find((stage) => stage.status === 'running');

    if (state.busy) {
      const index = running ? PIPELINE_STAGES.findIndex((meta) => meta.id === running.id) + 1 : 0;
      return {
        label: running ? `Running ${stageMeta(running.id).title.toLowerCase()}…` : 'Running…',
        icon: 'plant',
        title: 'A stage is running. Cancel stops after it finishes.',
        step: running ? `Stage ${index} of ${PIPELINE_STAGES.length}` : 'Running',
        hint: 'Every prompt, reply and artifact is being kept as it goes — open any stage to watch.',
        tone: 'busy',
        disabled: true,
        urgent: false,
        onClick: () => undefined,
      };
    }

    const blocked = this.blockedReason();
    if (blocked !== null) {
      return {
        label: 'Run all five stages',
        icon: 'plant',
        title: blocked,
        step: 'Not ready',
        hint: blocked,
        tone: 'blocked',
        disabled: true,
        urgent: false,
        onClick: () => undefined,
      };
    }

    // A complete run is the only state where creating the site is the obvious next move. Before
    // that, a compiled result exists as soon as stage 1 lands, and offering it as *the* action would
    // invite taking a site whose planting and structures have not been read yet.
    if (this.runner.complete && this.compiled !== null) {
      const report = this.compiled.report;
      return {
        label: 'Create site',
        icon: 'new',
        title: 'Build this reconstruction as the current site',
        step: 'All five stages done',
        hint: `${report.plantCount} plants and ${report.siteObjectCount} built objects are ready to place.`,
        tone: 'create',
        disabled: false,
        urgent: true,
        onClick: () => {
          if (this.compiled) this.deps.onCreate(this.compiled.project);
        },
      };
    }

    const done = state.stages.filter((stage) => stage.status === 'done').length;
    const failed = state.stages.find((stage) => stage.status === 'failed');
    const started = done > 0 || failed !== undefined;

    return {
      label: started ? 'Run the remaining stages' : 'Run all five stages',
      icon: 'plant',
      title: 'Identification → terrain → surfaces → planting → structures, as five calls',
      step: started ? `${done} of ${PIPELINE_STAGES.length} stages done` : 'Ready to run',
      hint:
        failed !== undefined
          ? `${stageMeta(failed.id).title} failed — fix it and run on, or re-run that stage alone.`
          : this.tagger.summary.total === 0
            ? 'Tagging the photograph first costs a minute and steers all five. Running now is fine too.'
            : 'Five calls, a couple of minutes. Every stage can be re-run on its own afterwards.',
      tone: 'run',
      disabled: false,
      urgent: !started,
      onClick: () => void this.runner.run(started ? this.firstUnfinishedStage() : 'segment'),
    };
  }

  /** Where "run the remaining stages" starts from: the earliest stage without an artifact. */
  private firstUnfinishedStage(): PipelineStageId {
    const state = this.runner.state;
    return (
      PIPELINE_STAGES.find(
        (meta) => state.stages.find((stage) => stage.id === meta.id)?.status !== 'done',
      )?.id ?? 'segment'
    );
  }

  /* ---------------------------------- Behaviour ---------------------------------- */

  private canRun(): boolean {
    return this.blockedReason() === null;
  }

  /**
   * Why a run cannot start, or null when it can.
   *
   * Two conditions, and both are genuine impossibilities rather than advice: with no photograph
   * there is nothing to read, and with no key there is nothing to read it. Tagging used to be a
   * third and is not any more — see `reference/ReferenceTag.ts` for why a step that a model can
   * draft cannot justify a gate.
   */
  private blockedReason(): string | null {
    if (this.attachments.length === 0) return 'Add a reference photograph to start.';
    if (!this.keyStore.has()) return 'Add an OpenAI API key to start.';
    return null;
  }

  /* -------------------------------- Suggestions -------------------------------- */

  /** The same two conditions, for the tagger's own button. */
  private suggestBlockedReason(): string | null {
    if (this.attachments.length === 0) return 'Add a reference photograph first.';
    if (!this.keyStore.has()) return 'Add an OpenAI API key first.';
    return null;
  }

  /**
   * One cheap call that fills the tagging panel in.
   *
   * Only the first photograph is sent, because every coordinate that comes back is about that image
   * — it is the one the tagger draws on and the one the overlay registers against. The direction box
   * goes with it: the designer has usually already typed what the place is, and a pre-pass that
   * ignored it would contradict them on the half it guessed.
   *
   * The model dropdown above is deliberately *not* passed through. It chooses the model for the
   * five reconstruction stages, which is a judgement about a whole site; this is one small
   * extraction from one photograph, and letting it ride along meant the cheapest call in the
   * pipeline ran on the flagship every time the dialog was left on its default.
   */
  private async suggestTags(signal: AbortSignal): Promise<SuggestTagsResult> {
    const apiKey = this.keyStore.get();
    const photo = this.attachments[0];
    if (!apiKey) throw new Error('there is no API key stored.');
    if (!photo) throw new Error('there is no reference photograph yet.');

    return suggestReferenceTags({
      apiKey,
      images: [photo.file],
      signal,
      imageCount: this.attachments.length,
      direction: this.direction,
      ...(this.frame ? { frame: this.frame } : {}),
    });
  }

  /**
   * Compile whatever the stages have produced so far.
   *
   * Run after every stage rather than only at the end, so the footer's plant count and warnings
   * appear as soon as the stage that decides them lands — the compiler is local and free, and a
   * warning is most useful while its stage is still the one on screen.
   */
  private recompile(): void {
    if (!this.runner.artifacts.segment) {
      this.compiled = null;
      this.compileError = null;
      return;
    }
    try {
      this.compiled = compilePipeline(this.runner.artifacts, {
        catalog: this.deps.catalog,
        seed: this.seed,
        // The designer's marks were written about this place; they belong on the site it becomes,
        // not just in the prompts that built it.
        referenceTags: this.tagger.tags,
      });
      this.compileError = null;
    } catch (error) {
      this.compiled = null;
      this.compileError =
        error instanceof Error ? error.message : 'The stages could not be compiled into a site.';
    }
  }

  /** Prompt preview must never break the view, even when an upstream artifact is malformed. */
  private safePrompt(stage: PipelineStageId): { system: string; user: string } | null {
    try {
      return this.runner.previewPrompt(stage);
    } catch {
      return null;
    }
  }

  /**
   * A captured map frame becomes the reference photograph.
   *
   * It takes the first slot, replacing any previous capture but keeping photographs the user added
   * themselves — a map frame plus a couple of ground-level shots of the same site is a genuinely
   * good input, and the first image is the one the overlay and the tagger work on.
   */
  private adoptFrame(file: File, frame: MapFrame): void {
    const previous = this.attachments[0];
    const attachment = { file, url: URL.createObjectURL(file) };
    if (previous && this.frame !== null) {
      URL.revokeObjectURL(previous.url);
      this.attachments[0] = attachment;
    } else {
      this.attachments.unshift(attachment);
      while (this.attachments.length > MAX_IMAGES) {
        const dropped = this.attachments.pop();
        if (dropped) URL.revokeObjectURL(dropped.url);
      }
    }
    this.frame = frame;
    this.syncTaggerPhoto();
    // Straight on to the required step, since the reason to be on this pane is now satisfied.
    this.selected = 'tags';
    this.render();
  }

  private addFiles(list: FileList | null): void {
    if (!list) return;
    for (const file of Array.from(list)) {
      if (!file.type.startsWith('image/')) continue;
      if (this.attachments.length >= MAX_IMAGES) break;
      this.attachments.push({ file, url: URL.createObjectURL(file) });
    }
    this.syncTaggerPhoto();
    this.render();
  }

  private removeAt(index: number): void {
    const [removed] = this.attachments.splice(index, 1);
    if (removed) URL.revokeObjectURL(removed.url);
    // The georeference belongs to the first image. Once that is gone the extent is a guess again,
    // and a prompt still claiming it is measured would be the worst of both.
    if (index === 0) this.frame = null;
    this.syncTaggerPhoto();
    this.render();
  }

  /**
   * The first photograph is the one tagged, and the one the overlay draws on.
   *
   * A no-op while it stays the same, so adding a second or third reference keeps the tags placed on
   * the first. Replacing the first drops them, which is right: a coordinate on one photograph is a
   * lie about another.
   */
  private syncTaggerPhoto(): void {
    const first = this.attachments[0];
    this.tagger.setPhoto(first?.url ?? null, first?.file.name);
  }
}

/* ---------------------------------- Helpers ---------------------------------- */

const TAB_LABELS: Record<DetailTab, string> = {
  overlay: 'Overlay',
  artifact: 'Artifact',
  reply: 'Reply',
  prompt: 'Prompt',
};

/** What each ribbon state means, spelled out — a coloured dot with no tooltip explains nothing. */
const RIBBON_STATUS: Record<string, string> = {
  done: 'done',
  running: 'running now',
  pending: 'not run yet',
  failed: 'failed',
  cancelled: 'cancelled',
  measured: 'supplied by measurement, no call needed',
  optional: 'optional, not used',
  skipped: 'optional, not used',
  todo: 'not done yet',
};

/**
 * A stage's state as a filled chip rather than four grey words.
 *
 * The status carried as data rather than baked into a class name, so one rule in `pipeline.css`
 * colours every one of them and a new state cannot be added without also being given a colour.
 */
function statusPill(status: string, label: string): HTMLElement {
  return el('span', { class: 'lg-stage__status', dataset: { status }, text: label });
}

function statusLabel(status: StageRun['status']): string {
  switch (status) {
    case 'pending':
      return 'not run';
    case 'running':
      return 'running…';
    case 'done':
      return 'done';
    case 'failed':
      return 'failed';
    case 'cancelled':
      return 'cancelled';
  }
}

function shapeNode(shape: OverlayShape): SVGElement {
  const ns = 'http://www.w3.org/2000/svg';

  if (shape.kind === 'point') {
    const [point] = shape.points;
    const circle = document.createElementNS(ns, 'circle');
    circle.setAttribute('cx', String((point?.[0] ?? 0) * 1000));
    circle.setAttribute('cy', String((point?.[1] ?? 0) * 1000));
    // Non-scaling stroke and an explicit radius keep control points legible at any aspect ratio,
    // which a `preserveAspectRatio="none"` viewBox would otherwise distort into ellipses.
    circle.setAttribute('r', '8');
    circle.setAttribute('fill', shape.color);
    circle.setAttribute('fill-opacity', '0.85');
    circle.setAttribute('stroke', '#ffffff');
    circle.setAttribute('stroke-width', '2');
    circle.setAttribute('vector-effect', 'non-scaling-stroke');
    circle.appendChild(title(`${shape.label} — ${shape.detail ?? ''}`));
    return circle;
  }

  const node = document.createElementNS(ns, shape.kind === 'polygon' ? 'polygon' : 'polyline');
  node.setAttribute('points', svgPoints(shape.points));
  node.setAttribute('stroke', shape.color);
  node.setAttribute('stroke-width', shape.kind === 'polyline' ? '4' : '2');
  node.setAttribute('vector-effect', 'non-scaling-stroke');
  node.setAttribute(
    'fill',
    shape.kind === 'polygon' ? shape.color : 'none',
  );
  // Confidence drives opacity, so a region the model was unsure of reads as unsure.
  node.setAttribute('fill-opacity', String(0.12 + 0.28 * shape.confidence));
  node.appendChild(title(`${shape.label}${shape.detail ? ` — ${shape.detail}` : ''}`));
  return node;
}

function title(text: string): SVGElement {
  const node = document.createElementNS('http://www.w3.org/2000/svg', 'title');
  node.textContent = text;
  return node;
}

function codeBlock(text: string): HTMLElement {
  const field = el('textarea', {
    class: 'lg-pipeline__code',
    attrs: { readonly: true, spellcheck: 'false', 'aria-label': 'Stage output' },
  });
  field.value = text;
  return el('div', { class: 'lg-pipeline__codewrap' }, field);
}

function promptText(prompt: { system: string; user: string } | null): string {
  if (!prompt) return 'The prompt cannot be built yet — an earlier stage has not produced its output.';
  return `${prompt.system}\n\n--- USER MESSAGE ---\n\n${prompt.user}`;
}

function stat(label: string, value: string): HTMLElement {
  return el(
    'div',
    { class: 'lg-field' },
    el('span', { class: 'lg-field__label', text: label }),
    el('span', { class: 'lg-field__value', text: value }),
  );
}

/**
 * Where the ground came from, beside the relief it produced.
 *
 * The number on its own is ambiguous in the way that matters most — 1.8 m of relief read off a
 * survey and 1.8 m invented by a model look identical in a stat tile — so the provenance travels
 * with it, and measured ground states its coverage because a partly-surveyed site is the case worth
 * catching.
 */
function describeGround(report: PipelineCompileResult['report']): string {
  switch (report.groundSource) {
    case 'measured':
      return `measured, ${Math.round(report.measuredCoverage * 100)}% covered`;
    case 'control-points':
      return `${report.sculptedPoints} points`;
    default:
      return 'procedural';
  }
}

/**
 * Materials the surfaces stage had made, as chips under the stage row.
 *
 * Worth its own line rather than being folded into the artifact JSON, because it is the one part of
 * a run that spent money on something the user did not explicitly ask for. Seeing "Red Decomposed
 * Granite · researched, rendered" is what makes that legible; a material that fell back to a guess
 * says so in the same place, so the difference between a researched colour and an invented one is
 * never something you have to go and check.
 */
function materialsRow(run: StageRun): HTMLElement | false {
  const materials = run.materials ?? [];
  if (materials.length === 0) return false;

  return el(
    'span',
    { class: 'lg-stage__materials' },
    ...materials.map((material) =>
      el(
        'span',
        {
          class: `lg-stage__material${material.error !== undefined ? ' is-failed' : ''}`,
          title: material.error ?? `Asked for as “${material.requestedId}” — ${material.detail ?? 'made'}`,
        },
        el('span', {
          class: 'lg-stage__swatch',
          style: { background: material.color ?? 'transparent' },
        }),
        el('span', { text: material.name }),
      ),
    ),
  );
}

/** Painted coverage as a short list, biggest first, base grass omitted. */
function describeCoverage(coverage: Record<SurfaceLayerId, number>): string {
  const painted = surfaceLayerIds().filter((id) => id !== 'grass')
    .map((id) => ({ id, share: coverage[id] ?? 0 }))
    .filter((entry) => entry.share > 0.001)
    .sort((a, b) => b.share - a.share);
  if (painted.length === 0) return 'all grass';
  return painted.map((entry) => `${entry.id} ${Math.round(entry.share * 100)}%`).join(' · ');
}

function formatTokens(total: number): string {
  return total >= 1000 ? `${(total / 1000).toFixed(1)}k` : String(total);
}

function readStoredModel(): string {
  try {
    return globalThis.localStorage?.getItem(MODEL_STORAGE_KEY) ?? DEFAULT_SITE_PLAN_MODEL;
  } catch {
    return DEFAULT_SITE_PLAN_MODEL;
  }
}

function storeModel(model: string): void {
  try {
    if (model.length > 0) globalThis.localStorage?.setItem(MODEL_STORAGE_KEY, model);
    else globalThis.localStorage?.removeItem(MODEL_STORAGE_KEY);
  } catch {
    // Non-fatal: the model choice holds for this session only.
  }
}
